/**
 * incidentsView.ts — pure view-model helpers for Operations / Incidents (M3).
 *
 * The wire stores severity and status as lowercase slugs (0101's CHECKs);
 * this module owns the labels — the modal's Low/Medium/High/Critical
 * (klaxiora-crm-mockup-full.html :3102-3107) — the count line (:5072), and
 * the KPI computations (:5065-5079). Two honesty rules over the mockup:
 * "Resolved this month" is genuinely month-scoped on resolvedAt (the
 * mockup's counts every resolved incident, :5069), and avg resolution time
 * is COMPUTED from resolvedAt - createdAt or '—' — never the mockup's
 * hardcoded "6h — Demo figure" (:1954).
 */
import type { OpsIncident } from '../../types'

export const INCIDENT_SEVERITIES = [
  { slug: 'low',      label: 'Low' },
  { slug: 'medium',   label: 'Medium' },
  { slug: 'high',     label: 'High' },
  { slug: 'critical', label: 'Critical' },
] as const

export function severityLabel(slug: string): string {
  return INCIDENT_SEVERITIES.find(s => s.slug === slug)?.label ?? slug
}

/** Critical/High get the danger treatment — the mockup's sevClass rule
 *  (:5090). */
export function severityIsSevere(slug: string): boolean {
  return slug === 'high' || slug === 'critical'
}

export function incidentCountLine(n: number): string {
  return `${n} incident${n !== 1 ? 's' : ''} logged.`
}

/** Mean resolution time over RESOLVED incidents as a compact label
 *  ("42m" / "6h" / "3d"), or '—' with none — never a fabricated figure. */
export function avgResolutionLabel(incidents: OpsIncident[]): string {
  const spans = incidents
    .filter(i => i.status === 'resolved' && i.resolvedAt)
    .map(i => new Date(i.resolvedAt as string).getTime() - new Date(i.createdAt).getTime())
    .filter(ms => ms >= 0)
  if (spans.length === 0) return '—'
  const ms = spans.reduce((a, b) => a + b, 0) / spans.length
  const minutes = Math.round(ms / 60_000)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.round(ms / 3_600_000)
  if (hours < 48) return `${hours}h`
  return `${Math.round(ms / 86_400_000)}d`
}

export interface IncidentKpis {
  open: number
  severeOpen: number
  resolvedThisMonth: number
  avgResolution: string
}

export function incidentKpis(incidents: OpsIncident[], now: Date): IncidentKpis {
  const open = incidents.filter(i => i.status === 'open')
  const resolvedThisMonth = incidents.filter(i => {
    if (i.status !== 'resolved' || !i.resolvedAt) return false
    const d = new Date(i.resolvedAt)
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
  }).length
  return {
    open: open.length,
    severeOpen: open.filter(i => severityIsSevere(i.severity)).length,
    resolvedThisMonth,
    avgResolution: avgResolutionLabel(incidents),
  }
}
